import {
    getCleanedPathname,
    getRoomlistSortCookieValue,
    getVerboseGenderPath,
    isMobileFilterPanelActive,
    SortOption,
    UrlState,
} from "@multimediallc/cb-roomlist-prefetch"
import { Gender, parseSimpleGender } from "@multimediallc/gender-utils"
import { getCookieOrEmptyString, setCookieWithDomain } from "@multimediallc/web-utils/storage"
import { addEventListenerPoly } from "../../../../common/addEventListenerPolyfill"
import { modalConfirm } from "../../../../common/alerts"
import { isAnonymous } from "../../../../common/auth"
import { Component } from "../../../../common/defui/component"
import { ignoreMetaClick } from "../../../../common/DOMutils"
import { EventRouter } from "../../../../common/events"
import { isMobileDmActive } from "../../../../common/featureFlagUtil"
import { followingEvent } from "../../../../common/follow"
import { MobileDmsMenu } from "../../../../common/mobilelib/mobileDmsMenu"
import { siteHeaderMenuOpened, toggleDms } from "../../../../common/mobilelib/userActionEvents"
import { addPageAction } from "../../../../common/newrelic"
import { addNoScroll, removeNoScroll } from "../../../../common/noScroll"
import { LOGIN_BASE_PATH, SIGN_UP_BASE_PATH } from "../../../../common/redirectParamUtils"
import { i18n } from "../../../../common/translation"
import { dom, Fragment } from "../../../../common/tsxrender/dom"
import { dmsEnabled } from "../../../api/pm"
import { pageContext } from "../../../interfaces/context"
import { currentSiteSettings } from "../../../siteSettings"
import { hashtagsUrl } from "../../../util/hashtagsUtils"
import { MobileUserUpdatesPanel } from "./userUpdatesPanel"

interface MobilePushMenuProps { menuButton: HTMLDivElement }

const TRANSITION_MS = 150  // Match transition in `mobile_menu.scss`
const ANIMATE_CLASS = "pushmenu-animate"
const MENU_EXPANDED_CONTENT_CLASS = "push-page-content"

export class MobilePushMenu extends Component<HTMLElement, MobilePushMenuProps> {
    protected menuButton: HTMLDivElement
    protected menuContent: HTMLElement
    protected pushOverlay: HTMLDivElement
    private _desktopLink: HTMLAnchorElement
    private socialIconsContainer: HTMLElement | null
    private followingLink: HTMLElement | null
    private lastScrollTop = 0
    private static _isOpen = false
    public static closeMenu = new EventRouter<void>("closeMobilePushMenu")

    protected initUI(props: MobilePushMenuProps): void {
        this.menuButton = props.menuButton
        this.menuContent = this.element.querySelector<HTMLElement>(".main-menu")!
        this.pushOverlay = this.element.querySelector<HTMLDivElement>(".push-overlay")!
        this._desktopLink = this.element.querySelector<HTMLAnchorElement>("#mobile_desktop_link")!
        this.socialIconsContainer = this.element.querySelector("#mobile-social-media-icons")
        this.followingLink = this.element.querySelector("#follow_page_link")

        this.menuButton.addEventListener("click", this.onMenuOpen.bind(this))
        this.pushOverlay.addEventListener("click", this.onMenuClosed.bind(this))
        this.bindUserUpdatesPanel()
        this.bindDmsMenu()
        this.bindHeaderOpenedEvents()
        this.bindLogoutLink()
        this.bindDesktopLink()
        this.bindSocialMediaIcons()
        this.bindFollowCountUpdates()

        this.bindRoomlistLinks()
        this.listenForCloseEvents()
    }

    protected listenForCloseEvents(): void {
        MobilePushMenu.closeMenu.listen(() => {
            this.onMenuClosed()
        })
    }

    protected onMenuOpen(): void {
        this.showElement()
        this.pushOverlay.style.display = "block"
        document.querySelectorAll(".content, header").forEach((el) => {
            el.classList.add(MENU_EXPANDED_CONTENT_CLASS)
        })
        this.setScroll(true)

        // wait for pushmenu display before adding transition
        window.setTimeout(() => {
            this.menuContent.classList.add(ANIMATE_CLASS)
        })

        addPageAction("MobileMenuClick", { "is_orange": this.menuButton.classList.contains("has_unseen") })
        siteHeaderMenuOpened.fire(true)
    }

    protected onMenuClosed(): void {
        this.menuContent.classList.remove(ANIMATE_CLASS)
        this.pushOverlay.style.opacity = "0"  // Opacity transition handled in scss
        document.querySelectorAll(".content, header").forEach((el) => {
            el.classList.remove(MENU_EXPANDED_CONTENT_CLASS)
        })
        this.setScroll(false)
        window.setTimeout(() => {
            this.hideElement()
            this.pushOverlay.style.opacity = ""  // Transition finished
        }, TRANSITION_MS)
        siteHeaderMenuOpened.fire(false)
    }

    public showElement(): void {
        super.showElement()
        MobilePushMenu._isOpen = true
    }

    public hideElement(): void {
        super.hideElement()
        MobilePushMenu._isOpen = false
    }

    private setScroll(isMenuOpen: boolean): void {
        if (isMenuOpen) {
            this.lastScrollTop = window.scrollY
            addNoScroll()
            document.body.style.top = `-${this.lastScrollTop}px`
        } else {
            removeNoScroll()
            document.body.style.top = ""
            window.scrollTo(0, this.lastScrollTop)
        }
    }

    public static isOpen(): boolean {
        return MobilePushMenu._isOpen
    }

    protected bindUserUpdatesPanel(): void {
        if (currentSiteSettings.isWhiteLabel) {
            return
        }

        const userUpdatesBellRoot = this.element.querySelector<HTMLDivElement>("#userUpdatesBellRoot")
        const userUpdatesDropDownRoot = this.element.querySelector<HTMLDivElement>("#userUpdatesMenuDropdownRoot")

        if (userUpdatesBellRoot !== null && userUpdatesDropDownRoot !== null) {
            const userUpdatesPanel = new MobileUserUpdatesPanel(userUpdatesBellRoot)
            userUpdatesPanel.element.dataset.testid = "user-updates-panel"
            userUpdatesPanel.initAdditionalToggleElement(this.menuButton)

            userUpdatesDropDownRoot.appendChild(userUpdatesPanel.dropDownComponent.element)
        }
    }

    protected bindDmsMenu(): void {
        if (pageContext.current.isAgeGated) {
            return
        }

        const messagingIconRoot = document.querySelector<HTMLDivElement>("#messagingIconRoot")
        let dmListIconRoot = this.element.querySelector<HTMLDivElement>("#dmListIconRoot")
        dmListIconRoot = isMobileDmActive() && isAnonymous() && messagingIconRoot ? messagingIconRoot : dmListIconRoot

        const userUpdatesBellRoot = this.element.querySelector<HTMLDivElement>("#userUpdatesBellRoot")
        const dmListDropDownRoot = this.element.querySelector<HTMLDivElement>("#dmListDropdownRoot")

        if (dmListIconRoot !== null && dmListDropDownRoot !== null) {
            dmListDropDownRoot.appendChild(new MobileDmsMenu({
                dmListIconRoot,
                userUpdatesBellRoot,
                hamburgerIcon: this.menuButton,
            }).element)
        }
    }

    private bindLogoutLink(): void {
        const logoutLink = document.querySelector<HTMLAnchorElement>("#logout-conf")
        const logoutForm = document.querySelector<HTMLFormElement>("#user_logout_form")

        if (logoutLink === null || logoutForm === null || isAnonymous()) {
            return
        }

        addEventListenerPoly("click", logoutLink, (event) => {
            event.preventDefault()
            modalConfirm(i18n.logoutModalText, () => {logoutForm.submit()})
        })
    }

    protected bindHeaderOpenedEvents(): void {
        toggleDms.listen((shouldOpen: boolean) => {
            if (shouldOpen && !MobilePushMenu._isOpen) {
                this.onMenuOpen()
            } else if (!shouldOpen && MobilePushMenu._isOpen) {
                this.onMenuClosed()
            }
        })
    }

    private bindDesktopLink(): void {
        const domain = this.desktopLink.dataset.domain ?? window.location.hostname
        this.desktopLink.addEventListener("click", () => {
            setCookieWithDomain("mobile_redirect", "never", { hours: 18 }, "/", domain)
            setCookieWithDomain("mobile_redirect_source", "user", { hours: 18 }, "/", domain)
            addPageAction("Mobile_DesktopLinkClicked")
        })
    }

    private bindSocialMediaIcons(): void {
        if (this.socialIconsContainer !== null) {  // Not rendered on whitelabels
            this.socialIconsContainer.querySelectorAll<HTMLAnchorElement>("a").forEach((el) => {
                el.addEventListener("click", () => {
                    addPageAction("ClickSocialMediaLink", {
                        "attributes.source": "mobile",
                        "attributes.clicked_socialmedia": el.dataset["social-type"],
                    })
                })
            })
        }
    }

    // Replaces the inline JS follow count logic in roomlist.html
    private bindFollowCountUpdates(): void {
        if (this.followingLink === null) {
            return  // Not rendered for logged-out users
        }
        const totalEl = this.followingLink.querySelector("#follow_count_total")!
        const onlineEl = this.followingLink.querySelector("#follow_count_online")!
        followingEvent.listen((event) => {
            const totalCount = event.totalCount ?? 0
            const onlineCount = event.onlineCount ?? 0
            if (totalCount > 0) {
                this.followingLink!.style.display = "block"
                onlineEl.textContent = `${onlineCount}`
                totalEl.textContent = `${totalCount}`
            } else {
                this.followingLink!.style.display = "none"
            }
        })
    }

    protected bindRoomlistLinks(): void {
        this.element.querySelectorAll(".pushmenu-link.ts-roomlist-link a:not([data-spa-ignore])").forEach((el) => {
            el.addEventListener("click", (event) => {
                ignoreMetaClick(event as MouseEvent, () => {
                    event.preventDefault()
                    this.onMenuClosed()
                    // TODO(CBD-2599) SPA handling not correctly initialized when starting on room pages
                    const url = new URL((event.currentTarget as HTMLAnchorElement).href, window.location.origin)
                    const sortValue = getRoomlistSortCookieValue()
                    if (sortValue !== SortOption.Popular) {
                        url.searchParams.set("sort", sortValue)
                    } else {
                        url.searchParams.delete("sort")
                    }
                    UrlState.current.navigateTo(url.toString())
                })
            })
        })
    }

    get desktopLink(): HTMLAnchorElement {
        return this._desktopLink
    }
}

export class FullMobilePushMenu extends MobilePushMenu {
    private messagingIconRoot: HTMLDivElement | undefined
    private dmListIconRoot: HTMLDivElement | undefined
    private userUpdatesBellRoot: HTMLDivElement | undefined
    private userUpdatesMenuDropdownRoot: HTMLDivElement
    private dmListDropdownRoot: HTMLDivElement | undefined

    protected createBaseElement(): HTMLDivElement {
        const showDms = pageContext.current.loggedInUser?.isStaff === true || dmsEnabled()

        return (
            <div id="pushmenu-container" data-testid="push-menu-container">
                <div className="push-overlay" onClick={() => this.onMenuClosed()} ref={(el: HTMLDivElement) => this.pushOverlay = el}></div>
                <nav className="pushmenu pushmenu-left main-menu" ref={(el: HTMLElement) => this.menuContent = el}>
                    <div className="top-box">
                        {this.createHeaderLinks()}
                        {this.createMenuLinks()}
                    </div>
                    <div className="bottom-box" data-testid="bottom-box">
                        {this.createBottomBoxContent()}
                    </div>
                </nav>
                <div id="userUpdatesMenuDropdownRoot" className="pushmenu pushmenu-left" ref={(el: HTMLDivElement) => this.userUpdatesMenuDropdownRoot = el} />
                {showDms && <div id="dmListDropdownRoot" className="pushmenu pushmenu-left" ref={(el: HTMLDivElement) => this.dmListDropdownRoot = el} />}
            </div>
        )
    }

    protected initUI(props: MobilePushMenuProps): void {
        this.menuButton = props.menuButton
        this.menuButton.addEventListener("click", this.onMenuOpen.bind(this))
        this.bindUserUpdatesPanel()
        this.bindDmsMenu()
        this.bindHeaderOpenedEvents()
        this.bindRoomlistLinks()
        this.listenForCloseEvents()
    }

    private createHeaderLinks(): HTMLDivElement {
        return (
            <div id="header-links" data-testid="header" className={isAnonymous() ? "shorter" : ""}>
                {this.createUserInfo()}
                {!isAnonymous() && this.createUserStatus()}
                {!isAnonymous() && this.createUserTokens()}
            </div>
        )
    }

    private createMenuLinks(): DocumentFragment {
        const totalFollowed = pageContext.current.loggedInUser?.totalFollowed ?? 0
        const onlineFollowed = pageContext.current.loggedInUser?.onlineFollowed ?? 0

        return (
            <Fragment>
                <div className="pushmenu-link ts-roomlist-link">
                    <a data-testid="featured-chat-rooms-tab" href={`/${getVerboseGenderPath(Gender.All)}/`}>Featured Chat Rooms</a>
                </div>
                <div className="pushmenu-link ts-roomlist-link">
                    <a className="indent1" data-testid="women-tab" href={`/${getVerboseGenderPath(Gender.Female)}/`}>Women</a>
                </div>
                <div className="pushmenu-link ts-roomlist-link">
                    <a className="indent1" data-testid="men-tab" href={`/${getVerboseGenderPath(Gender.Male)}/`}>Men</a>
                </div>
                <div className="pushmenu-link ts-roomlist-link">
                    <a className="indent1" data-testid="couples-tab" href={`/${getVerboseGenderPath(Gender.Couple)}/`}>Couples</a>
                </div>
                <div className="pushmenu-link ts-roomlist-link">
                    <a className="indent1" data-testid="trans-tab" href={`/${getVerboseGenderPath(Gender.Trans)}/`}>Trans</a>
                </div>
                {isMobileFilterPanelActive() &&
                    <div className="pushmenu-link ts-roomlist-link">
                        <a data-testid="hashtags-tab" data-spa-ignore href={hashtagsUrl(Gender.All)}>Tags</a>
                    </div>
                }
                <div className="pushmenu-link ts-roomlist-link">
                    <a data-testid="private-shows-tab" href="/spy-on-cams/">Private Shows</a>
                </div>
                {!isAnonymous() &&
                    <Fragment>
                        {totalFollowed && <div className="pushmenu-link ts-roomlist-link" id="follow_page_link"><a data-testid="following-tab" href="/followed-cams/">Following (<span id="follow_count_online" data-testid="following-online">{onlineFollowed}</span>/<span id="follow_count_total" data-testid="following-total">{totalFollowed}</span>)</a></div>}
                        <div className="pushmenu-link"><a data-testid="my-collection-tab" href="/my_collection/">My Collection</a></div>
                        <div className="pushmenu-link"><a id="mobile-broadcast-tab" data-testid="broadcast-tab" href="/b/">Broadcast <span style={{ background: "#f47321", borderRadius: "2px", color: "#fff", padding: "0 4px 0 3px" }}>BETA</span></a></div>
                    </Fragment>
                }
                {isAnonymous() &&
                    <div className="pushmenu_container pushmenu_container--shorter">
                        <div className="pushmenu_infobox">
                            <a data-testid="sign-up-button" href={SIGN_UP_BASE_PATH} className="pushmenu_lisu-btn pushmenu_lisu-btn--sign-up" account-redirect>Sign Up</a>
                            <a data-testid="log-in-button" href={LOGIN_BASE_PATH} className="pushmenu_lisu-btn pushmenu_lisu-btn--log-in" account-redirect>Log In</a>
                        </div>
                    </div>
                }
            </Fragment>
        )
    }

    private createUserInfo(): HTMLDivElement {
        const truncateChars = (str: string, length: number): string => {
            if (str.length <= length) {
                return str
            }
            return `${str.slice(0, length)}...`
        }
        const displayUsername = truncateChars(pageContext.current.loggedInUser?.username ?? "", 24)

        return (
            <div id="user-info">
                {this.createUserGenderIcon()}
                {!isAnonymous() && <div data-testid="username">{displayUsername}</div>}
                {this.createFeatureIcons()}
            </div>
        )
    }

    private createUserStatus(): HTMLDivElement {
        const isSupporter = pageContext.current.loggedInUser?.isSupporter === true
        const upgradeLink = `/supporter/upgrade/?source=${pageContext.current.PurchaseEventSources["SUPPORTER_SOURCE_USER_INFO_PANEL"]}`
        const nonSupporterView =
            <Fragment>
                <div id="status">Basic Member</div>
                <div id="upgrade-link" className="float-right">
                    (<a data-testid="upgrade-link" href={upgradeLink} id="upgrade_user">Upgrade</a>)
                </div>
            </Fragment>

        return (
            <div id="user-status" data-testid="user-status">
                <div id="status-label">Status:</div>
                {isSupporter && <div id="status">Supporter</div>}
                {!isSupporter && nonSupporterView}
            </div>
        )
    }

    private createUserTokens(): HTMLDivElement {
        const tokenBalance = pageContext.current.loggedInUser?.tokenBalance ?? 0
        const purchaseEventSource = pageContext.current.PurchaseEventSources["TOKEN_SOURCE_USER_INFO_PANEL"]
        const getMoreLinkHref = `/tipping/purchase_tokens/?source=${purchaseEventSource}"`

        return (
            <div id="user-tokens" data-testid="user-tokens">
                <div id="tokens-label">You have:</div>
                <div id="token-balance"><span className="tokencount" updatable-count>{tokenBalance}</span> Tokens</div>
                <div id="getmore">(<a data-testid="get-more-link" href={getMoreLinkHref} id="purchase_tokens" data-purchase-event-source={purchaseEventSource}>Get More</a>)</div>
            </div>
        )
    }

    private createUserGenderIcon(): HTMLDivElement {
        const userGender = pageContext.current.loggedInUser?.gender
        const parsedGender = userGender !== undefined ? parseSimpleGender(userGender) : userGender
        const getClassName = (): string => {
            if (isAnonymous()) {
                return "icon-anon"
            }
            switch (parsedGender) {
                case Gender.Female:
                    return "icon-f"
                case Gender.OldTrans:
                    return "icon-s"
                case Gender.Couple:
                    return "icon-c"
                case Gender.Male:
                    return "icon-m"
                default:
                    return ""
            }
        }

        return (
            <div id="gender-menu" data-testid="gender-icon" className={getClassName()} />
        )
    }

    private createFeatureIcons(): DocumentFragment {
        const showDms = pageContext.current.loggedInUser?.isStaff === true || dmsEnabled()

        return (
            <Fragment>
                {/* TODO: add classname_has_unseen template tag */}
                {currentSiteSettings.siteName === "Chaturbate" && <div id="userUpdatesBellRoot" data-testid="user-updates-bell-icon" className="notification_bell mobile {% classname_has_unseen %}" ref={(el: HTMLDivElement) => this.userUpdatesBellRoot = el} />}
                {showDms && isMobileDmActive() && <div id="messagingIconRoot" data-testid="messaging-icon" className="messaging_icon" ref={(el: HTMLDivElement) => this.messagingIconRoot = el} />}
                {showDms && !isMobileDmActive() && <div id="dmListIconRoot" data-testid="direct-messages-icon" className="user_information_icon chat_bubble" ref={(el: HTMLDivElement) => this.dmListIconRoot = el} />}
            </Fragment>
        )
    }

    private createBottomBoxContent(): DocumentFragment {
        return (
            <Fragment>
                <div className="spacer"></div>
                <div>
                    <a className="bottom-link" target="_blank" data-testid="give-feedback-link" href="https://www.surveymonkey.com/r/SRHCPVM">Give Feedback</a>
                    <span className="bottom-link">|</span>
                    {!isAnonymous() &&
                        <Fragment>
                            <a id="logout-conf" className="bottom-link" data-testid="log-out-link" href="/auth/logout/">Log Out</a><form id="user_logout_form" action="/auth/logout/" method="post" style={{ display: "none" }}>{getCookieOrEmptyString("csrftoken")}</form>
                            <span className="bottom-link">|</span>
                        </Fragment>
                    }
                    {currentSiteSettings.isWhiteLabel && <a id="mobile_desktop_link" className="bottom-link" data-testid="desktop-site-link" href={getCleanedPathname()} target="_self">Desktop Site</a>}
                    {!currentSiteSettings.isWhiteLabel && <a id="mobile_desktop_link" className="bottom-link" data-testid="desktop-site-link" href={getCleanedPathname()} target="_self" data-domain={new URL(currentSiteSettings.canonicalBaseUrl).hostname}>Desktop Site</a>}
                </div>
                <div>
                    <a className="bottom-link" data-testid="support-link" href="http://support.chaturbate.com">Support</a>
                    <span className="bottom-link">|</span>
                    <a className="bottom-link" data-testid="privacy-policy-link" href="/privacy/">Privacy Policy</a>
                    <span className="bottom-link">|</span>
                    <a className="bottom-link" data-testid="terms-link" href="/terms/">Terms</a>
                </div>
                <div style={{ marginBottom: "15px" }}>
                    <a className="bottom-link" target="_blank" data-testid="report-abusive-content" href="https://support.chaturbate.com/hc/en-us/requests/new?ticket_form_id=26373221045780">Report Nonconsensual/Abusive Content</a>
                </div>
                {currentSiteSettings.siteName === "Chaturbate" &&
                    <ul id="mobile-social-media-icons">
                        <li><a data-testid="twitter-x-media-link" href="https://twitter.com/chaturbate" data-social-type="X" target="_blank"><img alt="X" src={`${STATIC_URL_ROOT}images/socialmediaicons-mobile/social-media-twitter-x.svg`} /></a></li>
                    </ul>
                }
                <div className="spacer"></div>
                <div className="bottom-link" style={{ marginBottom: "25px" }}>
                    <a className="bottom-link" data-testid="statement-link" href="/2257/">18 U.S.C. 2257 Record-Keeping Requirements Compliance Statement</a>
                </div>
                <div className="spacer"></div>
                <div className="spacer"></div>
                <div className="bottom-link" data-testid="copyright-text">&copy; Copyright Chaturbate.com 2011 - {new Date().getFullYear()}.<br/>All Rights Reserved. Patent
                    Pending.
                </div>
                <div className="bottom-spacer"></div>

            </Fragment>
        )
    }

    protected bindUserUpdatesPanel(): void {
        if (currentSiteSettings.isWhiteLabel) {
            return
        }


        if (this.userUpdatesBellRoot !== undefined && this.userUpdatesMenuDropdownRoot !== undefined) {
            const userUpdatesPanel = new MobileUserUpdatesPanel(this.userUpdatesBellRoot)
            userUpdatesPanel.element.dataset.testid = "user-updates-panel"
            userUpdatesPanel.initAdditionalToggleElement(this.menuButton)

            this.userUpdatesMenuDropdownRoot.appendChild(userUpdatesPanel.dropDownComponent.element)
        }
    }

    protected bindDmsMenu(): void {
        if (pageContext.current.isAgeGated) {
            return
        }

        const dmListIconRoot = isMobileDmActive() && isAnonymous() && this.messagingIconRoot ? this.messagingIconRoot : this.dmListIconRoot

        if (dmListIconRoot !== undefined && this.dmListDropdownRoot !== undefined) {
            this.dmListDropdownRoot.appendChild(new MobileDmsMenu({
                dmListIconRoot,
                userUpdatesBellRoot: this.userUpdatesBellRoot ?? null, // eslint-disable-line @multimediallc/no-null-usage
                hamburgerIcon: this.menuButton,
            }).element)
        }
    }

    protected bindRoomlistLinks(): void {
        // Remove after roomlist/room spa merge
        if (UrlState.current.state.room !== undefined) {
            return
        }

        super.bindRoomlistLinks()
    }
}
